/**
 * Untamed — Instant Navigation (PJAX + View Transitions) — all internal routes
 * ============================================================================
 * Intercepts same-origin internal links and swaps #main-content in place for
 * an instant, app-like transition, instead of a full page reload.
 *
 * RE-INIT MODEL — GENERIC, NO COMPONENT ALLOWLIST:
 *   After swapping #main-content we (1) re-execute every section/block script
 *   that lives inside the swapped content (with a DOMContentLoaded/load shim so
 *   their auto-init runs), and (2) dispatch shopify:section:unload for the old
 *   sections and shopify:section:load for the new ones. Persistent singletons
 *   loaded in <head>/header/footer (cart drawer, color swatches, icons, sticky,
 *   offers drawer, …) are NOT re-executed — they self-re-init by listening to
 *   shopify:section:load. We never hand-pick individual components to rebind.
 *
 * DESIGN PRINCIPLE — ROBUSTNESS OVER COVERAGE: any link / route that can't be
 * safely swapped falls back to a full navigation (location.href). Every
 * fetch / parse / swap error falls back to a full navigation, so this layer can
 * never hard-break the site.
 */
(function () {
  'use strict';

  // --- Bail conditions ----------------------------------------------------
  if (window.Shopify && window.Shopify.designMode) return;
  if (!('fetch' in window) || !('DOMParser' in window) || !window.history || !window.history.pushState) return;
  if (window.__untamedInstantNav) return;
  window.__untamedInstantNav = true;

  var MAIN_SELECTOR = '#main-content';
  var mainEl = document.querySelector(MAIN_SELECTOR);
  if (!mainEl) return;

  var htmlCache = new Map();
  var snapshots = new Map();
  var MAX_CACHE = 24;

  function trimCache(map) {
    while (map.size > MAX_CACHE) {
      var firstKey = map.keys().next().value;
      map.delete(firstKey);
    }
  }

  function cleanUrl(href) {
    try { return new URL(href, window.location.href).href; } catch (e) { return null; }
  }

  // --- Persistent (singleton) scripts -------------------------------------
  // Any <script src> that lives OUTSIDE #main-content (head/header/footer) is a
  // global singleton; it must not be re-executed on swap. Such singletons
  // self-re-init via the shopify:section:load events we dispatch below.
  function scriptName(src) {
    try { return new URL(src, window.location.href).pathname.split('/').pop(); } catch (e) { return String(src); }
  }
  var persistentScripts = Object.create(null);
  Array.prototype.forEach.call(document.querySelectorAll('script[src]'), function (s) {
    if (!mainEl.contains(s)) persistentScripts[scriptName(s.getAttribute('src') || s.src)] = true;
  });

  // --- Route eligibility --------------------------------------------------
  var ALLOW_PREFIXES = ['/collections', '/products', '/pages', '/blogs', '/search', '/policies'];
  var DENY_PREFIXES = [
    '/cart/', '/account', '/checkout', '/checkouts', '/admin', '/apps', '/tools',
    '/services', '/wpm', '/.well-known', '/gift_cards', '/orders', '/community', '/recommendations'
  ];
  var FILE_RE = /\.(pdf|zip|jpe?g|png|gif|webp|svg|mp4|webm|mov|csv|xml|json|txt|woff2?|ico|rss|atom)$/i;

  function isAllowedPath(pathname) {
    var i;
    for (i = 0; i < DENY_PREFIXES.length; i++) {
      if (pathname.indexOf(DENY_PREFIXES[i]) === 0) return false;
    }
    if (pathname === '/') return true;
    if (pathname === '/cart') return true;
    for (i = 0; i < ALLOW_PREFIXES.length; i++) {
      var p = ALLOW_PREFIXES[i];
      if (pathname === p || pathname.indexOf(p + '/') === 0) return true;
    }
    return false;
  }

  var CONTROL_SELECTOR = [
    'button', 'select', 'input', 'textarea', 'label', 'summary',
    '.shopify-payment-button', '[data-shopify="payment-button"]',
    '[data-add-to-cart]', '[data-quick-add]', '[data-quick-view]',
    '[data-wishlist-btn]', '[data-bundle-product]',
    '[data-color-select-mount]', '[data-color-select]',
    '[data-sort-select]', '[data-remove-filter]', '[data-view-toggle]',
    '[data-no-instant-nav]'
  ].join(', ');

  function eligibleAnchor(target) {
    if (!target || !target.closest) return null;
    if (target.closest(CONTROL_SELECTOR)) return null;

    var a = target.closest('a[href]');
    if (!a) return null;

    var raw = a.getAttribute('href');
    if (!raw) return null;
    if (/^(mailto:|tel:|sms:|javascript:|#)/i.test(raw)) return null;

    var url = cleanUrl(a.href);
    if (!url) return null;

    var u;
    try { u = new URL(url); } catch (e) { return null; }
    if (u.origin !== window.location.origin) return null;
    if (u.hash) return null;
    if (a.target && a.target !== '' && a.target !== '_self') return null;
    if (a.hasAttribute('download')) return null;
    if ((a.getAttribute('rel') || '').split(/\s+/).indexOf('external') !== -1) return null;
    if (a.hasAttribute('data-no-instant-nav')) return null;
    if (FILE_RE.test(u.pathname)) return null;
    if (!isAllowedPath(u.pathname)) return null;

    return { anchor: a, url: url, isCard: !!a.closest('[data-product-card], [data-quick-view-card]') };
  }

  // --- Prefetch -----------------------------------------------------------
  function fetchHtml(url) {
    if (htmlCache.has(url)) return htmlCache.get(url);
    var p = fetch(url, { credentials: 'same-origin', headers: { 'X-Instant-Nav': '1' } })
      .then(function (res) {
        var ct = res.headers.get('content-type') || '';
        if (!res.ok) throw new Error('Bad status ' + res.status);
        if (ct.indexOf('text/html') === -1) throw new Error('Non-HTML response');
        return res.text();
      })
      .catch(function (err) {
        htmlCache.delete(url);
        throw err;
      });
    htmlCache.set(url, p);
    trimCache(htmlCache);
    return p;
  }

  function prefetch(url) {
    if (!url || htmlCache.has(url)) return;
    try { fetchHtml(url); } catch (e) {}
  }

  function onIntent(e) {
    var hit = eligibleAnchor(e.target);
    if (hit && hit.url !== cleanUrl(window.location.href)) prefetch(hit.url);
  }

  // --- Generic script re-execution ----------------------------------------
  // Re-runs every eligible script inside the swapped content. A DOMContentLoaded
  // /load/readystatechange shim makes scripts that auto-init on those events run
  // their init immediately (the document is already "complete" during a swap).
  function runScripts(scope) {
    var scripts = Array.prototype.slice.call(scope.querySelectorAll('script'));

    var origDocAdd = document.addEventListener;
    var origWinAdd = window.addEventListener;
    function patchedAdd(orig) {
      return function (type, cb, opts) {
        if (type === 'DOMContentLoaded' || type === 'load' || type === 'readystatechange') {
          try {
            if (typeof cb === 'function') { cb({ type: type }); }
            else if (cb && cb.handleEvent) { cb.handleEvent({ type: type }); }
          } catch (err) {}
          return;
        }
        return orig.call(this, type, cb, opts);
      };
    }

    function execNext(i) {
      if (i >= scripts.length) return Promise.resolve();
      var old = scripts[i];

      var typeAttr = (old.getAttribute('type') || '').toLowerCase();
      if (typeAttr && typeAttr !== 'text/javascript' && typeAttr !== 'module' && typeAttr !== 'application/javascript') {
        return execNext(i + 1);
      }

      if (old.src) {
        var nm = scriptName(old.getAttribute('src') || old.src);
        if (persistentScripts[nm]) return execNext(i + 1);
        var fresh = document.createElement('script');
        for (var a = 0; a < old.attributes.length; a++) fresh.setAttribute(old.attributes[a].name, old.attributes[a].value);
        return new Promise(function (resolve) {
          fresh.onload = fresh.onerror = function () { resolve(); };
          old.parentNode.replaceChild(fresh, old);
        }).then(function () { return execNext(i + 1); });
      }

      var inline = document.createElement('script');
      for (var b = 0; b < old.attributes.length; b++) inline.setAttribute(old.attributes[b].name, old.attributes[b].value);
      inline.textContent = old.textContent;
      document.addEventListener = patchedAdd(origDocAdd);
      window.addEventListener = patchedAdd(origWinAdd);
      try { old.parentNode.replaceChild(inline, old); } catch (err) {}
      finally { document.addEventListener = origDocAdd; window.addEventListener = origWinAdd; }
      return execNext(i + 1);
    }

    return execNext(0);
  }

  function dispatchSection(el, name, sectionId) {
    try { el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { sectionId: sectionId, instantNav: true } })); } catch (e) {}
  }

  // Generic re-init: re-execute swapped scripts, then fire shopify:section:load
  // for the new sections so every persistent singleton self-re-binds. No
  // component allowlist. (shopify:section:unload is fired in applyHtml, while the
  // old nodes are still attached, so it can bubble to document-level listeners.)
  function reinit() {
    return runScripts(mainEl).then(function () {
      try {
        var sections = mainEl.querySelectorAll('.shopify-section');
        if (sections.length) {
          Array.prototype.forEach.call(sections, function (sec) {
            dispatchSection(sec, 'shopify:section:load', (sec.id || '').replace('shopify-section-', ''));
          });
        } else {
          dispatchSection(mainEl, 'shopify:section:load', null);
        }
      } catch (e) {}
    });
  }

  // --- Head update --------------------------------------------------------
  function copyMetaAttr(doc, selector, attr) {
    try {
      var fresh = doc.querySelector(selector);
      var cur = document.querySelector(selector);
      if (fresh && cur) cur.setAttribute(attr, fresh.getAttribute(attr) || '');
      else if (fresh && !cur && document.head) document.head.appendChild(fresh.cloneNode(true));
    } catch (e) {}
  }
  function updateHead(doc) {
    try { var t = doc.querySelector('title'); if (t) document.title = t.textContent; } catch (e) {}
    copyMetaAttr(doc, 'link[rel="canonical"]', 'href');
    copyMetaAttr(doc, 'meta[name="description"]', 'content');
    copyMetaAttr(doc, 'meta[property="og:title"]', 'content');
    copyMetaAttr(doc, 'meta[property="og:url"]', 'content');
    copyMetaAttr(doc, 'meta[property="og:description"]', 'content');
  }

  // --- Analytics ----------------------------------------------------------
  function firePageView() {
    function go() {
      try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish('page_viewed', {}); } catch (e) {}
    }
    if (document.prerendering) document.addEventListener('prerenderingchange', go, { once: true });
    else go();
  }

  // --- Swap ---------------------------------------------------------------
  function parseDoc(html) { return new DOMParser().parseFromString(html, 'text/html'); }

  function applyHtml(newMainHtml, doc, scrollY) {
    // Fire shopify:section:unload while the old sections are still attached, so
    // the event bubbles to document-level listeners that tear them down.
    try {
      Array.prototype.forEach.call(mainEl.querySelectorAll('.shopify-section'), function (sec) {
        dispatchSection(sec, 'shopify:section:unload', (sec.id || '').replace('shopify-section-', ''));
      });
    } catch (e) {}
    mainEl.innerHTML = newMainHtml;
    if (doc) updateHead(doc);
    window.scrollTo(0, scrollY || 0);
  }

  function swap(newMainHtml, doc, scrollY, afterReinit) {
    function done() { if (afterReinit) { try { afterReinit(); } catch (e) {} } }
    if (document.startViewTransition) {
      try {
        var vt = document.startViewTransition(function () { applyHtml(newMainHtml, doc, scrollY); });
        vt.updateCallbackDone.then(function () { return reinit(); }).then(done).catch(function () { reinit().then(done).catch(done); });
        return;
      } catch (e) {}
    }
    applyHtml(newMainHtml, doc, scrollY);
    reinit().then(done).catch(done);
  }

  function navigateTo(url, isPop, restoreScroll) {
    var currentUrl = cleanUrl(window.location.href);
    if (currentUrl && !isPop) {
      snapshots.set(currentUrl, { html: mainEl.innerHTML, title: document.title, scrollY: window.scrollY });
      trimCache(snapshots);
    }

    if (isPop && snapshots.has(url)) {
      var snap = snapshots.get(url);
      swap(snap.html, null, typeof restoreScroll === 'number' ? restoreScroll : snap.scrollY, firePageView);
      if (snap.title) document.title = snap.title;
      return;
    }

    fetchHtml(url)
      .then(function (html) {
        var doc = parseDoc(html);
        var newMain = doc.querySelector(MAIN_SELECTOR);
        if (!newMain) throw new Error('No main container in response');
        if (!isPop) window.history.pushState({ instantNav: true, url: url }, '', url);
        swap(newMain.innerHTML, doc, isPop ? restoreScroll : 0, firePageView);
      })
      .catch(function () {
        if (isPop) window.location.reload();
        else window.location.href = url;
      });
  }

  // --- Click interception -------------------------------------------------
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var hit = eligibleAnchor(e.target);
    if (!hit) return;
    if (hit.url === cleanUrl(window.location.href)) return;

    e.preventDefault();
    if (hit.isCard) e.stopImmediatePropagation();
    navigateTo(hit.url, false);
  }, true);

  document.addEventListener('touchstart', onIntent, { passive: true, capture: true });
  document.addEventListener('pointerdown', onIntent, { passive: true, capture: true });
  document.addEventListener('mouseenter', onIntent, { passive: true, capture: true });

  // --- Back / forward -----------------------------------------------------
  if ('scrollRestoration' in window.history) {
    try { window.history.scrollRestoration = 'manual'; } catch (e) {}
  }

  window.addEventListener('popstate', function () {
    var url = cleanUrl(window.location.href);
    if (!url) return;
    var u;
    try { u = new URL(url); } catch (e) { window.location.reload(); return; }
    if (!snapshots.has(url) && !isAllowedPath(u.pathname)) { window.location.href = url; return; }
    var restoreScroll = (snapshots.get(url) || {}).scrollY || 0;
    navigateTo(url, true, restoreScroll);
  });
})();
